import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, Link2, Star, Flame, Lightbulb, ChevronRight, GraduationCap, Hourglass, Sparkles, Stethoscope, Zap } from 'lucide-react';
import type { AcademyTraining } from '../../services/medewerkerApi';
import { ACCENT_KLEUR, LEERVORM, WKR_ORANJE, accentVoor, datumKort, isHobby, prijs } from './academyStijl';

type Status = AcademyTraining['beschikbaarheid']['status'];

const STATUS_KLEUR: Record<Status, { dot: string; tekst: string }> = {
  direct: { dot: 'bg-emerald-400', tekst: 'text-emerald-400' },
  open: { dot: 'bg-emerald-400', tekst: 'text-emerald-400' },
  plekken: { dot: 'bg-amber-400', tekst: 'text-amber-300' },
  vol: { dot: 'bg-red-500', tekst: 'text-red-400' },
};

export function useStatusLabel() {
  const { t } = useTranslation('medewerker');
  return (tr: AcademyTraining): string => {
    const b = tr.beschikbaarheid;
    switch (b.status) {
      case 'direct':
        return t('academy.tegel.direct', { defaultValue: 'Direct starten' });
      case 'plekken':
        return b.vrije_plekken === 1
          ? t('academy.tegel.nogEen', { defaultValue: 'Nog 1 plek' })
          : t('academy.tegel.nogPlekken', { defaultValue: 'Nog {{n}} plekken', n: b.vrije_plekken ?? 0 });
      case 'vol':
        return t('academy.tegel.vol', { defaultValue: 'VOL' });
      default:
        return b.eerstvolgende_start
          ? t('academy.tegel.start', { defaultValue: 'Start {{datum}}', datum: datumKort(b.eerstvolgende_start) })
          : t('academy.tegel.aanmelden', { defaultValue: 'Aanmelden open' });
    }
  };
}

/** Bovenste laag: 16:9-cover met leervorm-badge (links) en review-score + 🔥 (rechts). */
const TegelCover: React.FC<{ training: AcademyTraining; groot?: boolean }> = ({ training, groot }) => {
  const { t } = useTranslation('medewerker');
  const vorm = LEERVORM[training.leervorm];
  const Icon = vorm.icon;
  const accent = ACCENT_KLEUR[accentVoor(training)];
  return (
    <div className="relative aspect-video overflow-hidden bg-[#10151f]">
      {training.omslagfoto_url ? (
        <img src={training.omslagfoto_url} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" draggable={false} />
      ) : (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ background: `radial-gradient(120% 90% at 30% 20%, ${accent}55 0%, #0b1020 60%, #05070c 100%)` }}
          aria-hidden="true"
        >
          <span className={groot ? 'text-7xl' : 'text-5xl'}>{training.subcategorie_emoji || training.categorie_emoji}</span>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent" aria-hidden="true" />

      <span
        className={`absolute left-2.5 top-2.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white shadow-lg ${vorm.badge}`}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {t(`academy.leervorm.${training.leervorm}`, { defaultValue: vorm.label })}
      </span>

      {training.geadviseerd && (
        <span className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1 rounded-full border border-amber-300/50 bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-amber-200 backdrop-blur">
          <Stethoscope className="h-3 w-3" aria-hidden="true" />
          {t('academy.tegel.geadviseerd', { defaultValue: 'Geadviseerd door uw casemanager' })}
        </span>
      )}
      <div className="absolute right-2.5 top-2.5 flex items-center gap-1.5">
        {training.populair && (
          <span className="inline-flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-[11px] font-semibold text-orange-300 backdrop-blur">
            <Flame className="h-3.5 w-3.5 animate-pulse text-orange-400" aria-hidden="true" />
            {t('academy.tegel.populair', { defaultValue: 'Populair' })}
          </span>
        )}
        {training.rating && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-[11px] font-bold text-white backdrop-blur"
            title={t('academy.tegel.reviews', { defaultValue: '{{n}} reviews', n: training.rating.aantal })}
          >
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
            {training.rating.gemiddelde.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
          </span>
        )}
      </div>
    </div>
  );
};

/** Onderste laag: donkerblauwe informatiebalk met titel, opleider-link, prijs, duur en status. */
const TegelBalk: React.FC<{ training: AcademyTraining; titelKnop?: React.ReactNode }> = ({ training, titelKnop }) => {
  const { t } = useTranslation('medewerker');
  const statusLabel = useStatusLabel();
  const kleur = STATUS_KLEUR[training.beschikbaarheid.status];
  return (
    <div className="bg-[#0A192F] px-3.5 pb-3 pt-2.5">
      {titelKnop ?? <h3 className="line-clamp-2 text-[15px] font-bold leading-snug text-white">{training.titel}</h3>}
      <p className="mt-1 truncate text-xs text-slate-300">
        {t('academy.tegel.opleider', { defaultValue: 'Opleider:' })}{' '}
        <Link
          to={`/medewerker/academy/opleider/${training.provider_id}`}
          onClick={(e) => e.stopPropagation()}
          className="relative z-10 inline-flex items-center gap-1 text-sky-300 underline decoration-sky-300/40 underline-offset-2 hover:text-sky-200"
        >
          <Link2 className="h-3 w-3" aria-hidden="true" />
          {training.organisatie_naam}
        </Link>
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        {training.korting_bedrag > 0 && <s className="text-slate-400">{prijs(training.bruto_bedrag)}</s>}
        <span className="text-sm font-bold text-white">{prijs(training.netto_bedrag)}</span>
        {training.korting_bedrag > 0 && (
          <span className="rounded-full border border-emerald-500/50 bg-emerald-500/10 px-1.5 py-px text-[10px] font-medium text-emerald-300">
            {t('academy.tegel.korting', { defaultValue: '5% BeLagom korting' })}
          </span>
        )}
      </div>
      <div className="mt-1.5 flex items-center gap-2 text-xs">
        {training.duur && (
          <>
            <span className="inline-flex min-w-0 items-center gap-1 text-slate-300">
              <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{training.duur}</span>
            </span>
            <span className="h-3.5 w-px shrink-0 bg-white/15" aria-hidden="true" />
          </>
        )}
        <span className={`inline-flex shrink-0 items-center gap-1.5 font-medium ${kleur.tekst}`}>
          <span className={`h-2 w-2 rounded-full ${kleur.dot}`} aria-hidden="true" />
          {statusLabel(training)}
        </span>
      </div>
    </div>
  );
};

/** Uitgeklapte onderbalk bij hover: one-liner, voordeel-callout en metadata-badges. */
const HoverPaneel: React.FC<{ training: AcademyTraining; onOpen: () => void }> = ({ training, onOpen }) => {
  const { t } = useTranslation('medewerker');
  const vorm = LEERVORM[training.leervorm];
  const Icon = vorm.icon;
  const b = training.beschikbaarheid;
  return (
    <div className="border-t border-white/10 bg-[#0A192F] px-3.5 pb-3.5 pt-3">
      {training.pitch && (
        <p className="flex items-start gap-2 text-[13px] font-semibold leading-snug text-white">
          <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" aria-hidden="true" />
          <span className="line-clamp-2 flex-1">{training.pitch}</span>
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
        </p>
      )}
      {training.korting_bedrag > 0 && (
        <p className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-amber-300/30 bg-amber-300/10 px-2 py-1 text-[11px] font-medium text-amber-200">
          <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
          {t('academy.tegel.voordeel', {
            defaultValue: 'Inclusief 5% BeLagom Voordeel (u bespaart {{bedrag}})',
            bedrag: prijs(training.korting_bedrag),
          })}
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[11px] text-slate-100">
          <Icon className="h-3 w-3" aria-hidden="true" />
          {t(`academy.leervorm.${training.leervorm}`, { defaultValue: vorm.label })}
        </span>
        {training.certificaat && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600/90 px-2 py-0.5 text-[11px] font-medium text-white">
            <GraduationCap className="h-3 w-3" aria-hidden="true" />
            {t('academy.tegel.certificaat', { defaultValue: 'Certificaat inclusief' })}
          </span>
        )}
        {b.status === 'plekken' && (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[11px] font-medium text-amber-300">
            <Hourglass className="h-3 w-3" aria-hidden="true" />
            {t('academy.tegel.nogMaar', { defaultValue: 'Nog maar {{n}} plekken!', n: b.vrije_plekken ?? 0 })}
          </span>
        )}
        {training.academy_tags.slice(0, 2).map((tag) => (
          <span key={tag} className="rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[11px] text-slate-300">
            # {tag}
          </span>
        ))}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#E5A425] px-3 py-2 text-sm font-bold text-[#0A192F] shadow-[0_0_18px_rgba(229,164,37,.45)] transition hover:bg-[#F2B53A] hover:brightness-110"
      >
        <Zap className="h-4 w-4 fill-current" aria-hidden="true" />
        {t('academy.tegel.directBoeken', { defaultValue: 'Direct Anoniem Boeken' })}
      </button>
    </div>
  );
};

const HOVER_VERTRAGING_MS = 280;
const SCHAAL = 1.06;

/**
 * Cursustegel (Netflix-stijl). Default: 16:9-cover + donkerblauwe informatiebalk.
 * Hover (desktop) of eerste tik (touch): zwevende kopie op 106% met rijkleur-rand
 * en uitgeklapte onderbalk — gerenderd in een portal zodat de horizontale
 * swiper hem niet afknipt. Klik → Informatie-scherm (detail-pop-up).
 */
export const AcademyTegel: React.FC<{
  training: AcademyTraining;
  onOpen: (training: AcademyTraining) => void;
  className?: string;
}> = ({ training, onOpen, className = '' }) => {
  const ref = useRef<HTMLElement>(null);
  const timer = useRef<number | null>(null);
  const [preview, setPreview] = useState<DOMRect | null>(null);
  const hobby = isHobby(training);

  const kanHoveren = () => typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;

  const stopTimer = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => stopTimer, []);

  const toonPreview = useCallback(() => {
    if (ref.current) setPreview(ref.current.getBoundingClientRect());
  }, []);
  const sluit = useCallback(() => setPreview(null), []);

  return (
    <>
      <article
        ref={ref}
        onMouseEnter={() => {
          if (!kanHoveren()) return;
          stopTimer();
          timer.current = window.setTimeout(toonPreview, HOVER_VERTRAGING_MS);
        }}
        onMouseLeave={stopTimer}
        className={`relative shrink-0 snap-start overflow-hidden rounded-xl bg-[#0A192F] shadow-md shadow-slate-900/10 ring-1 transition duration-200 ${
          hobby ? '' : 'ring-slate-900/5'
        } ${preview ? 'opacity-0' : ''} ${className}`}
        // Harde randgrendel rij 4: flinterdunne, gedimde oranje rand (WKR-risico)
        style={hobby ? { boxShadow: `0 0 0 1px ${WKR_ORANJE}66, 0 4px 6px -1px rgba(10,25,47,.1)` } : undefined}
      >
        <TegelCover training={training} />
        <TegelBalk
          training={training}
          titelKnop={
            <h3 className="line-clamp-2 text-[15px] font-bold leading-snug text-white">
              <button
                type="button"
                onClick={() => (kanHoveren() ? onOpen(training) : toonPreview())}
                className="text-left after:absolute after:inset-0 after:content-[''] focus:outline-none focus-visible:after:rounded-xl focus-visible:after:ring-2 focus-visible:after:ring-sky-400"
              >
                {training.titel}
              </button>
            </h3>
          }
        />
      </article>
      {preview && <HoverPreview training={training} anker={ref} rect={preview} onSluit={sluit} onOpen={() => onOpen(training)} />}
    </>
  );
};

/** Zwevende 106%-kopie van de tegel, gepositioneerd over het origineel. */
const HoverPreview: React.FC<{
  training: AcademyTraining;
  anker: React.RefObject<HTMLElement | null>;
  rect: DOMRect;
  onSluit: () => void;
  onOpen: () => void;
}> = ({
  training,
  anker,
  rect,
  onSluit,
  onOpen,
}) => {
  const { t } = useTranslation('medewerker');
  const paneel = useRef<HTMLDivElement>(null);
  const [actief, setActief] = useState(false);
  const [verschuiving, setVerschuiving] = useState(0);
  const accent = ACCENT_KLEUR[accentVoor(training)];
  const touch = typeof window !== 'undefined' && !window.matchMedia('(hover: hover)').matches;

  // Schaal vanaf de rand als de tegel tegen de zijkant van het scherm staat.
  const marge = (rect.width * (SCHAAL - 1)) / 2 + 12;
  const originX = rect.left < marge ? 'left' : window.innerWidth - rect.right < marge ? 'right' : 'center';

  useLayoutEffect(() => {
    const frame = requestAnimationFrame(() => setActief(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  // Houd de uitgeklapte tegel binnen het scherm (onderkant), gemeten na de uitklap-animatie.
  useEffect(() => {
    if (!actief) return;
    const id = window.setTimeout(() => {
      if (!paneel.current) return;
      const over = paneel.current.getBoundingClientRect().bottom - (window.innerHeight - 12);
      if (over > 0) setVerschuiving(-over);
    }, 320);
    return () => window.clearTimeout(id);
  }, [actief]);

  useEffect(() => {
    // Alleen sluiten als de tegel echt verschuift (scroll-snap/sub-pixel events negeren).
    const sluit = () => {
      const nu = anker.current?.getBoundingClientRect();
      if (!nu || Math.abs(nu.left - rect.left) > 6 || Math.abs(nu.top - rect.top) > 6) onSluit();
    };
    const toets = (e: KeyboardEvent) => e.key === 'Escape' && onSluit();
    window.addEventListener('scroll', sluit, true);
    window.addEventListener('resize', sluit);
    window.addEventListener('keydown', toets);
    return () => {
      window.removeEventListener('scroll', sluit, true);
      window.removeEventListener('resize', sluit);
      window.removeEventListener('keydown', toets);
    };
  }, [onSluit, anker, rect]);

  return createPortal(
    <>
      {touch && <div className="fixed inset-0 z-40" onClick={onSluit} aria-hidden="true" />}
      <div
        className="fixed z-50"
        style={{ left: rect.left, top: rect.top + verschuiving, width: rect.width }}
        onMouseLeave={touch ? undefined : onSluit}
      >
        <div
          ref={paneel}
          role="button"
          tabIndex={-1}
          onClick={onOpen}
          aria-label={t('academy.tegel.openDetail', { defaultValue: 'Meer informatie over {{titel}}', titel: training.titel })}
          className="cursor-pointer overflow-hidden rounded-xl bg-[#0A192F] transition-[transform,box-shadow,translate] duration-300 ease-out"
          style={{
            transformOrigin: `${originX} 35%`,
            transform: actief ? `scale(${SCHAAL})` : 'scale(1)',
            boxShadow: actief
              ? `0 0 0 1.5px ${accent}, 0 20px 40px -12px rgba(10,25,47,.45)`
              : '0 4px 6px -1px rgba(10,25,47,.1)',
          }}
        >
          <TegelCover training={training} />
          <TegelBalk training={training} />
          <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${actief ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
            <div className="overflow-hidden">
              <HoverPaneel training={training} onOpen={onOpen} />
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default AcademyTegel;
